import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CircleCheckBig, CircleX, Loader2, X, Pencil } from "lucide-react";
import {
  useGetOneExpedition,
  useGetOneExpeditionBo,
  useReassignExpeditionCreatedBy,
} from "@/api/expedition.api";
import {
  GetAllBillingEntityDto,
  GetOneExpeditionBoResponseDto,
  GetOneExpeditionResponseDto,
} from "@/types/api/api-types";
import Paths from "@/constants/paths";
import { Separator } from "../ui/separator";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentListItem } from "@/components/shared/documentListItem";
import { DocumentsUpdateModal } from "@/components/shared/modal/documentsUpdate.modal";
import { useGetCompany, useGetCompanyBOV1 } from "@/api/company.api";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { useAtomValue } from "jotai";
import { userAtom } from "@/stores/authStore";
import { useGetUsers } from "@/api/user.api";
import { Modal } from "@/components/ui/customs/modal";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { ExpeditionBlockedReasonEnum } from "@/constants/enums/expeditionBlockedReason.enum";
import {
  BO_DETAIL_EXPEDITION,
  FileAction,
  FO_DETAIL_EXPEDITION,
  getVisibleFileTypes,
  sortDocumentsByPriority,
} from "@/constants/fileCrud";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { useOtherCustomQuotationTypeOptions } from "@/hooks/useEnumOptions";
import { useExpeditionBlockedReasonLabels } from "@/hooks/useEnumLabels";

type Props = {
  open: boolean;
  onClose: () => void;
  expeditionId: string;
  companyId?: string;
};

type PropsBo = Props & {
  forcefullEdit?: () => void; // Used to bypass the draft restriction
};

type DetailModalContentProps = Omit<Props, "expeditionId"> & {
  expedition: GetOneExpeditionResponseDto | GetOneExpeditionBoResponseDto;
  billingEntities: GetAllBillingEntityDto;
  forcefullEdit?: () => void;
  isBo?: boolean;
};

const ExpeditionOption = ({ option, activated }: { option: string; activated: boolean }) => {
  return (
    <div
      className={cn(
        "flex w-fit flex-row items-center gap-2 rounded-xl px-2 py-1 text-sm font-semibold",
        activated
          ? "border border-blue-500 bg-blue-50 text-blue-500"
          : "border border-gray-500 bg-gray-50 text-gray-500",
      )}
    >
      {activated ? <CircleCheckBig className="h-4 w-4" /> : <CircleX className="h-4 w-4" />}
      {option}
    </div>
  );
};

const DetailInfo = ({ label, value, last }: { label: string; value: string; last?: boolean }) => {
  return (
    <div
      key={`${label}-${value}`}
      className={cn(
        "grid w-full grid-cols-2 justify-items-start gap-2 border-b-[1px] pt-3",
        last && "border-b-0",
        !last && "pb-3",
      )}
    >
      <p className="text-xs font-medium text-gray-600">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
};

const TrackingInfo = ({ label, value, link }: { label: string; value?: string; link: string }) => {
  return (
    <div className="grid w-full grid-cols-2 justify-items-start gap-2 border-b-[1px] py-3">
      <p className="text-xs font-medium text-gray-600">{label}</p>
      {value && link ? (
        <Button
          variant="link"
          className="h-fit w-fit p-0 text-sm font-medium"
          onClick={() => window.open(link, "_blank")}
        >
          {value}
        </Button>
      ) : (
        <p className="text-sm font-medium">-</p>
      )}
    </div>
  );
};

export const DetailModalBo = ({
  open,
  onClose,
  expeditionId,
  companyId,
  forcefullEdit,
}: PropsBo) => {
  const { t } = useTranslation("shared");
  const { data: expedition, isLoading: isLoadingExpedition } = useGetOneExpeditionBo(expeditionId);
  const { data: company, isLoading: isLoadingCompany } = useGetCompanyBOV1(companyId || "");

  const isLoading = isLoadingCompany || isLoadingExpedition;

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className="max-h-[90vh] w-[80vw] min-w-[80vw] gap-0 overflow-y-auto p-0"
          aria-describedby={undefined}
          showClose={false}
        >
          <div className="hidden">
            <DialogTitle>{t("expeditionDetailModal.title")}</DialogTitle>
          </div>
          <div className="flex w-full flex-row items-center justify-between p-6">
            <h2 className="w-full self-center text-center text-xl font-bold">
              {t("expeditionDetailModal.title")}
            </h2>
            <button onClick={onClose}>
              <X />
            </button>
          </div>
          <Separator />
          <div className="flex w-full flex-row items-center justify-center gap-4 bg-blue-50 p-2">
            <Loader2 className="animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!expedition || !company) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className="max-h-[90vh] w-[80vw] min-w-[80vw] gap-0 overflow-y-auto p-0"
          aria-describedby={undefined}
          showClose={false}
        >
          <div className="hidden">
            <DialogTitle>{t("expeditionDetailModal.title")}</DialogTitle>
          </div>
          <div className="flex w-full flex-row items-center justify-between p-6">
            <h2 className="w-full self-center text-center text-xl font-bold">
              {t("expeditionDetailModal.title")}
            </h2>
            <button onClick={onClose}>
              <X />
            </button>
          </div>
          <Separator />
          <div className="flex w-full flex-row items-center justify-center gap-4 bg-blue-50 p-2">
            <p>{t("expeditionDetailModal.error")}</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <DetailModalContent
      open={open}
      onClose={onClose}
      expedition={expedition}
      billingEntities={company?.billingEntities || []}
      forcefullEdit={forcefullEdit}
      isBo
    />
  );
};

export const DetailModalClient = ({ open, onClose, expeditionId }: Props) => {
  const { t } = useTranslation("shared");
  const { data: expedition, isLoading: isLoadingExpedition } = useGetOneExpedition(expeditionId);
  const { data: company } = useGetCompany();

  if (isLoadingExpedition) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className="max-h-[90vh] w-[80vw] min-w-[80vw] gap-0 overflow-y-auto p-0"
          aria-describedby={undefined}
          showClose={false}
        >
          <div className="hidden">
            <DialogTitle>{t("expeditionDetailModal.title")}</DialogTitle>
          </div>
          <div className="flex w-full flex-row items-center justify-between p-6">
            <h2 className="w-full self-center text-center text-xl font-bold">
              {t("expeditionDetailModal.title")}
            </h2>
            <button onClick={onClose}>
              <X />
            </button>
          </div>
          <Separator />
          <div className="flex w-full flex-row items-center justify-center gap-4 bg-blue-50 p-2">
            <Loader2 className="animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!expedition) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className="max-h-[90vh] w-[80vw] min-w-[80vw] gap-0 overflow-y-auto p-0"
          aria-describedby={undefined}
          showClose={false}
        >
          <div className="hidden">
            <DialogTitle>{t("expeditionDetailModal.title")}</DialogTitle>
          </div>
          <div className="flex w-full flex-row items-center justify-between p-6">
            <h2 className="w-full self-center text-center text-xl font-bold">
              {t("expeditionDetailModal.title")}
            </h2>
            <button onClick={onClose}>
              <X />
            </button>
          </div>
          <Separator />
          <div className="flex w-full flex-row items-center justify-center gap-4 bg-blue-50 p-2">
            <p>{t("expeditionDetailModal.loadError")}</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <DetailModalContent
      open={open}
      onClose={onClose}
      expedition={expedition}
      billingEntities={company?.billingEntities || []}
    />
  );
};

const DetailModalContent = ({
  open,
  onClose,
  billingEntities,
  expedition,
  forcefullEdit,
  isBo = false,
}: DetailModalContentProps) => {
  const { t } = useTranslation("shared");
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-h top-0 left-0 flex h-dvh w-[100vw] min-w-[100vw] translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:top-1/2 sm:left-1/2 sm:w-[80vw] sm:min-w-[80vw] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:border md:h-[80%]"
        aria-describedby={undefined}
        showClose={true}
      >
        <div className="hidden">
          <DialogTitle>{t("expeditionDetailModal.title")}</DialogTitle>
        </div>
        <div className="flex w-full flex-col items-center gap-4 p-4 sm:flex-row sm:p-6">
          <h2 className="w-full text-xl font-bold">{t("expeditionDetailModal.title")}</h2>
          <div className="flex items-center gap-2 pr-4">
            {forcefullEdit && (
              <Button variant={"ghost"} onClick={forcefullEdit}>
                {t("expeditionDetailModal.edit")}
              </Button>
            )}
          </div>
        </div>
        <Separator />
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-8 overflow-y-auto bg-[#F8FAFC] p-6 xl:grid-cols-2">
          <div className="flex flex-col gap-4">
            <ExpeditionDetails expedition={expedition} />
            <ExpeditionValidation expedition={expedition} billingEntities={billingEntities} />
            {expedition.customQuotation?.type === CustomQuotationTypeEnum.PALETTE && (
              <ExpeditionPalette expedition={expedition} />
            )}
            {expedition.customQuotation?.type === CustomQuotationTypeEnum.PARCEL && (
              <ExpeditionParcel expedition={expedition} />
            )}
            {expedition.customQuotation?.type === CustomQuotationTypeEnum.OTHER && (
              <ExpeditionOther expedition={expedition} />
            )}
          </div>
          <div className="flex flex-col gap-4">
            <ExpeditionDocuments expedition={expedition} isBo={isBo} />
            <ExpeditionComment expedition={expedition} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ExpeditionDetails = ({
  expedition,
}: {
  expedition: GetOneExpeditionResponseDto | GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation("shared");
  const blockedReasonLabels = useExpeditionBlockedReasonLabels();
  const client =
    expedition.deliveryInfo?.firstName &&
    expedition.deliveryInfo?.lastName &&
    expedition.deliveryInfo?.firstName + " " + expedition.deliveryInfo?.lastName;
  const company = expedition.deliveryInfo?.company;

  const deliveryStatus = expedition.deliveryStatus;
  const blockedReason = expedition.blockedReason;

  return (
    <div className="flex flex-col gap-2 rounded-md p-5 sm:border sm:bg-white sm:shadow-xs">
      <h3 className="text-base font-semibold">{t("expeditionDetailModal.mainInfoTitle")}</h3>
      <div className="flex flex-wrap gap-2">
        {expedition.quotation && (
          <ExpeditionOption
            option={expedition.expeditionOptions.dapOption ? "DAP" : "DDP"}
            activated={true}
          />
        )}
        <ExpeditionOption
          option={t("expeditionDetailModal.expressOption")}
          activated={expedition.expeditionOptions.express}
        />
        {expedition.quotation && (
          <ExpeditionOption
            option={t("expeditionDetailModal.woodboxOption")}
            activated={
              (expedition.quotation.quotationWoodBox &&
                expedition.quotation.quotationWoodBox.length > 0) ||
              false
            }
          />
        )}
        <ExpeditionOption
          option={t("expeditionDetailModal.insuranceOption")}
          activated={expedition.expeditionOptions.insurance}
        />
      </div>
      <DetailInfo
        label={t("expeditionDetailModal.clientLabel")}
        value={`${client && company ? `${company} (${client})` : client || company || "-"}`}
      />
      <TrackingInfo
        label={t("expeditionDetailModal.trackingLabel")}
        value={
          expedition.tracking?.manualTracking
            ? t("expeditionDetailModal.trackingLink")
            : expedition.tracking?.trackingCode
        }
        link={
          expedition.tracking?.manualTracking
            ? expedition.tracking?.trackingUrl
            : `${Paths.others.tracking}?code=${expedition.id}`
        }
      />
      {deliveryStatus === DeliveryStatusEnum.FAILED && blockedReason && (
        <DetailInfo
          label={t("expeditionDetailModal.failureReasonLabel")}
          value={blockedReasonLabels[blockedReason as ExpeditionBlockedReasonEnum]}
        />
      )}
      <DetailInfo
        label={t("expeditionDetailModal.deliveryFromDateLabel")}
        value={
          expedition.deliveryInfo?.deliveryFromDate
            ? format(new Date(expedition.deliveryInfo?.deliveryFromDate), "dd/MM/yyyy")
            : "-"
        }
        last
      />
    </div>
  );
};

const ExpeditionValidation = ({
  expedition,
  billingEntities,
}: {
  expedition: GetOneExpeditionResponseDto | GetOneExpeditionBoResponseDto;
  billingEntities: GetAllBillingEntityDto;
}) => {
  const { t } = useTranslation(["shared", "common"]);
  const user = useAtomValue(userAtom);
  const canReassign = user?.role === "SUPERADMIN" && !user?.isBoUser;
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const { data: users } = useGetUsers(user?.isBoUser);
  const { mutate: reassignCreatedBy, isPending: isReassigning } = useReassignExpeditionCreatedBy();

  const billingEntity = billingEntities.find(
    (billingEntity) => billingEntity.id === expedition.expeditionBillingDetail?.billingEntityId,
  );

  const pickupLocation =
    expedition.pickupInfo?.pickupLocation?.name ||
    `${expedition.pickupInfo?.pickupStreet}, ${expedition.pickupInfo?.pickupPostalCode} ${expedition.pickupInfo?.pickupCity}, ${expedition.pickupInfo?.pickupCountry}`;

  const handleReassign = () => {
    if (!selectedUserId) return;
    reassignCreatedBy(
      { id: expedition.id, userId: selectedUserId },
      {
        onSuccess: () => {
          setReassignModalOpen(false);
          setSelectedUserId("");
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-md p-5 sm:border sm:bg-white sm:shadow-xs">
      <h3 className="text-base font-semibold">{t("expeditionDetailModal.validationTitle")}</h3>
      <div className="grid w-full grid-cols-2 justify-items-start gap-2 border-b-[1px] pt-3 pb-3">
        <p className="text-xs font-medium text-gray-600">{t("expeditionDetailModal.userLabel")}</p>
        <div className="flex w-full items-center justify-between">
          <p className="text-sm font-medium">
            {expedition.createdBy?.firstName + " " + expedition.createdBy?.lastName}
          </p>
          {canReassign && (
            <button onClick={() => setReassignModalOpen(true)}>
              <Pencil className="h-4 w-4 text-gray-500 hover:text-blue-500" />
            </button>
          )}
        </div>
      </div>
      <DetailInfo
        label={t("expeditionDetailModal.validatedAtLabel")}
        value={
          expedition.validatedAt
            ? format(new Date(expedition.validatedAt), "dd/MM/yyyy à HH:mm")
            : "-"
        }
      />
      <DetailInfo label={t("expeditionDetailModal.pickupLocationLabel")} value={pickupLocation} />
      <DetailInfo
        label={t("expeditionDetailModal.billingEntityLabel")}
        value={billingEntity?.companyName || "-"}
      />
      <DetailInfo
        label={t("expeditionDetailModal.referenceLabel")}
        value={expedition.expeditionBillingDetail?.reference || "-"}
        last
      />
      {reassignModalOpen && (
        <Modal
          open={reassignModalOpen}
          onClose={() => {
            setReassignModalOpen(false);
            setSelectedUserId("");
          }}
          title={t("expeditionDetailModal.reassignTitle")}
          size="sm"
          footer={
            <>
              <Button
                className="w-full"
                disabled={!selectedUserId || isReassigning}
                onClick={handleReassign}
              >
                {isReassigning ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t("common:actions.confirm")
                )}
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setReassignModalOpen(false);
                  setSelectedUserId("");
                }}
              >
                {t("common:actions.cancel")}
              </Button>
            </>
          }
        >
          <SelectV2
            value={selectedUserId}
            onChange={(value) => setSelectedUserId(value)}
            options={
              users?.map((u) => ({
                label: u.firstName + " " + u.lastName,
                value: u.id,
              })) ?? []
            }
            placeholder={t("expeditionDetailModal.reassignUserPlaceholder")}
            searchable
            searchPlaceholder={t("expeditionDetailModal.reassignUserSearchPlaceholder")}
          />
        </Modal>
      )}
    </div>
  );
};

const ExpeditionComment = ({
  expedition,
}: {
  expedition: GetOneExpeditionResponseDto | GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation("shared");
  return (
    <div className="flex w-full flex-col gap-3">
      <h3 className="text-base font-semibold">{t("expeditionDetailModal.commentTitle")}</h3>
      <div className="max-h-[200px] min-h-[60px] overflow-y-auto rounded-lg border-gray-300 p-2 text-sm text-gray-500 sm:border sm:bg-white">
        <p className="text-gray-900">
          {expedition.deliveryInfo?.comment || t("expeditionDetailModal.noComment")}
        </p>
      </div>
    </div>
  );
};

const ExpeditionDocuments = ({
  expedition,
  isBo = false,
}: {
  expedition: GetOneExpeditionResponseDto | GetOneExpeditionBoResponseDto;
  isBo?: boolean;
}) => {
  const { t } = useTranslation("shared");
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);

  const fileContextRules = isBo ? BO_DETAIL_EXPEDITION : FO_DETAIL_EXPEDITION;

  const sortedDocuments = useMemo(() => {
    const fileAction = isBo ? FileAction.BO_DETAIL_EXPEDITION : FileAction.FO_DETAIL_EXPEDITION;
    const readableFileTypes = getVisibleFileTypes(fileAction);
    const readableDocuments = (expedition.expeditionDocuments?.documents ?? []).filter((document) =>
      readableFileTypes.includes(document.type as FileTypeEnum),
    );
    return sortDocumentsByPriority(readableDocuments);
  }, [expedition.expeditionDocuments?.documents, isBo]);

  const invalidateQueryKey = isBo
    ? ["expedition", "bo", expedition.id]
    : ["expedition", expedition.id];

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">{t("expeditionDetailModal.documentsTitle")}</h3>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          type="button"
          title={t("expeditionDetailModal.editDocuments")}
          onClick={() => setDocumentsModalOpen(true)}
        >
          <Pencil className="size-4" />
        </Button>
      </div>
      {sortedDocuments.length > 0 ? (
        <div className="flex flex-col gap-1 rounded-lg border bg-white p-2 shadow-xs">
          {sortedDocuments.map((file) => (
            <DocumentListItem key={file.id} file={file} showDetailsInfo={isBo} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">{t("expeditionDetailModal.noDocuments")}</p>
      )}
      <DocumentsUpdateModal
        open={documentsModalOpen}
        onClose={() => setDocumentsModalOpen(false)}
        expeditionId={expedition.id}
        documents={sortedDocuments}
        expeditionDocumentsId={
          expedition.expeditionDocuments && "id" in expedition.expeditionDocuments
            ? expedition.expeditionDocuments.id
            : undefined
        }
        invalidateQueryKey={invalidateQueryKey}
        isBo={isBo}
        expedition={expedition}
        fileContextRules={fileContextRules}
      />
    </div>
  );
};

const ExpeditionPalette = ({
  expedition,
}: {
  expedition: GetOneExpeditionResponseDto | GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation("shared");
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-white p-5 shadow-xs">
      <h3 className="text-base font-semibold">{t("expeditionDetailModal.paletteTitle")}</h3>
      {expedition.customQuotation?.paletteDetail?.paletteProducts?.map((product, index) => (
        <DetailInfo
          key={`product-${index}`}
          label={t("expeditionDetailModal.productLabel", { index: index + 1 })}
          value={`${product.bottleQuantity} x ${product.volume}L`}
        />
      ))}
      {expedition.customQuotation?.paletteDetail?.palettePackages?.map((e, index) => (
        <DetailInfo
          key={`palette-${index}`}
          label={t("expeditionDetailModal.paletteLabel", { index: index + 1 })}
          value={`${e.length} x ${e.width} x ${e.height} | ${e.weight} kg | ${e.value} €`}
        />
      ))}
    </div>
  );
};

const ExpeditionParcel = ({
  expedition,
}: {
  expedition: GetOneExpeditionResponseDto | GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation("shared");
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-white p-5 shadow-xs">
      <h3 className="text-base font-semibold">{t("expeditionDetailModal.parcelTitle")}</h3>
      {expedition.customQuotation?.parcelDetail?.parcelProducts?.map((product, index) => (
        <DetailInfo
          label={t("expeditionDetailModal.productLabel", { index: index + 1 })}
          value={
            product.type === "BOTTLE"
              ? `${product.bottleQuantity} x ${product.volume}L`
              : `${product.description}`
          }
        />
      ))}
      {expedition.customQuotation?.parcelDetail?.parcelPackages?.map((e, index) => (
        <DetailInfo
          label={t("expeditionDetailModal.parcelLabel", { index: index + 1 })}
          value={`${e.length} x ${e.width} x ${e.height} | ${e.weight} kg | ${e.value} €`}
        />
      ))}
    </div>
  );
};

const ExpeditionOther = ({
  expedition,
}: {
  expedition: GetOneExpeditionResponseDto | GetOneExpeditionBoResponseDto;
}) => {
  const { t } = useTranslation("shared");
  const otherCustomQuotationOptions = useOtherCustomQuotationTypeOptions();
  return (
    <div className="flex flex-col gap-2 rounded-md border bg-white p-5 shadow-xs">
      <h3 className="text-base font-semibold">{t("expeditionDetailModal.otherTitle")}</h3>
      <div className="flex flex-row gap-2">
        <ExpeditionOption
          option={
            otherCustomQuotationOptions.find(
              (option) =>
                option.value === expedition.customQuotation?.otherDetail?.otherCustomQuotationType,
            )?.label || "-"
          }
          activated={true}
        />
        <ExpeditionOption
          option={t("expeditionDetailModal.multipleDeliveryOption")}
          activated={expedition.customQuotation?.otherDetail?.isMultipleExpedition || false}
        />
      </div>
      <DetailInfo
        label={t("expeditionDetailModal.numberOfExpeditionsLabel")}
        value={
          expedition.customQuotation?.otherDetail?.numberOfExpedition
            ? expedition.customQuotation?.otherDetail?.numberOfExpedition.toString()
            : "-"
        }
        last
      />
    </div>
  );
};
